import { Skeleton } from "@/components/ui/skeleton";

/**
 * Skeleton loader for cart items
 * Displays placeholder loading state while cart items are being fetched
 */
export function CartItemSkeleton() {
  return (
    <div className='flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6 p-4 rounded-lg border border-muted'>
      {/* Product Image Skeleton */}
      <Skeleton className='aspect-square w-full sm:w-40 rounded-md' />

      {/* Item Details Skeleton */}
      <div className='flex-1 space-y-4 w-full'>
        <div className='flex justify-between items-start'>
          <div className='space-y-2 w-3/4'>
            <Skeleton className='h-6 w-3/4' />
            <Skeleton className='h-4 w-1/2' />
          </div>
          <Skeleton className='h-8 w-8 rounded-md' />
        </div>

        <div className='flex justify-between items-end pt-2'>
          {/* Quantity Controls Skeleton */}
          <div className='flex items-center space-x-2'>
            <Skeleton className='h-8 w-8 rounded-md' />
            <Skeleton className='h-8 w-12 rounded-md' />
            <Skeleton className='h-8 w-8 rounded-md' />
          </div>

          {/* Price Skeleton */}
          <div className='text-right'>
            <Skeleton className='h-5 w-20 ml-auto' />
            <Skeleton className='h-4 w-16 ml-auto mt-1' />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton loader for the entire cart page
 * Displays multiple cart item skeletons and a summary skeleton
 */
export function CartPageSkeleton() {
  return (
    <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8'>
      <Skeleton className='h-10 w-64 mb-8' />

      <div className='grid grid-cols-1 lg:grid-cols-12 gap-8'>
        {/* Cart Items Skeleton */}
        <div className='lg:col-span-8'>
          <div className='space-y-4'>
            {Array.from({ length: 3 }).map((_, index) => (
              <CartItemSkeleton key={index} />
            ))}
          </div>
        </div>

        {/* Summary Skeleton */}
        <div className='lg:col-span-4'>
          <div className='bg-card p-6 rounded-lg border border-muted'>
            <Skeleton className='h-6 w-32 mb-6' />

            <div className='space-y-4'>
              <div className='flex justify-between'>
                <Skeleton className='h-4 w-20' />
                <Skeleton className='h-4 w-16' />
              </div>

              <div className='flex justify-between'>
                <Skeleton className='h-4 w-24' />
                <Skeleton className='h-4 w-16' />
              </div>

              <div className='flex justify-between'>
                <Skeleton className='h-4 w-16' />
                <Skeleton className='h-4 w-20' />
              </div>

              <div className='pt-4 border-t border-muted'>
                <div className='flex justify-between'>
                  <Skeleton className='h-5 w-20' />
                  <Skeleton className='h-5 w-24' />
                </div>
              </div>

              <Skeleton className='h-10 w-full mt-4' />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton loader for cart items in a sheet/drawer
 */
export function CartSheetSkeleton() {
  return (
    <div className='pr-2 space-y-4 w-full'>
      {Array.from({ length: 3 }).map((_, index) => (
        <div
          key={index}
          className='flex space-x-4 p-4 rounded-lg border border-muted w-full'
        >
          {/* Product Image Skeleton */}
          <Skeleton className='aspect-square w-16 h-16 rounded-md flex-shrink-0' />

          {/* Item Details Skeleton */}
          <div className='flex-1 space-y-2 min-w-0'>
            <Skeleton className='h-5 w-3/4' />
            <Skeleton className='h-4 w-1/2' />

            <div className='flex justify-between items-center pt-2'>
              <Skeleton className='h-4 w-16' />
              <Skeleton className='h-5 w-16' />
            </div>
          </div>
        </div>
      ))}

      <div className='pt-4 border-t border-muted w-full'>
        <div className='flex justify-between mb-4'>
          <Skeleton className='h-5 w-20' />
          <Skeleton className='h-5 w-24' />
        </div>

        <Skeleton className='h-10 w-full' />
      </div>
    </div>
  );
}
